import {
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  HStack,
  Text,
} from '@chakra-ui/react';
import {
  Card,
  CardContent,
  CardHead,
  CardTitle,
  SegmentedPicker,
} from '@payler/ui-components';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { FullWidthBlock, PageTitle } from '../layouts';
import React, { FC, Suspense } from 'react';
import styled from '@emotion/styled';
import { breakpoints } from '@payler/ui-theme';
import { Helmet } from 'react-helmet-async';
import {
  useFasterPayoutsBalancesQuery,
  useSelectedContract,
} from '../hooks/contracts';
import { BalancesStrip } from '../components/Balances/BalancesStrip';
import { MakeFasterPayoutForm } from '../forms/MakeFasterPayoutForm/MakeFasterPayoutForm';
import { McFasterPayoutsTable } from '../tables/FasterPayoutsTable/McFasterPayoutsTable';
import { useMcFasterPayoutStatusToast } from '../hooks/faster-payouts/use-mc-faster-payout-status-toast';

const Wrap = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas: 'form' 'table';
  grid-row-gap: 16px;
  @media (min-width: ${breakpoints.lg}) {
    display: grid;
    grid-template-columns: 320px 1fr;
    grid-template-areas: 'form table';
    align-items: flex-start;
    grid-column-gap: 16px;
  }
`;

const PAYOUT_MODE = ['single'] as const;
type TPayoutMode = (typeof PAYOUT_MODE)[number];

export const MakeFasterPayoutPage: FC<{ mode?: TPayoutMode }> = ({
  mode = 'single',
}) => {
  const { t } = useTranslation(['titles', 'makeFasterPayout']);
  useMcFasterPayoutStatusToast();

  return (
    <>
      <Helmet title={t('titles:makeFasterPayout')} />
      <Suspense fallback={null}>
        <PageHeader />
      </Suspense>
      <ModeSwitcher mode={mode} />
      <Suspense fallback={null}>
        <Balances />
      </Suspense>
      <Wrap>
        <TableCard />
        <FormCard />
      </Wrap>
    </>
  );
};

const ModeSwitcher: FC<{ mode: TPayoutMode }> = ({ mode }) => {
  const { t } = useTranslation(['makeFasterPayout']);
  const optionsLoc: Record<TPayoutMode | 'mass', string> = {
    single: t('makeFasterPayout:single'),
    mass: t('makeFasterPayout:mass'),
  };
  const options = [optionsLoc.single, optionsLoc.mass];

  return (
    <Box w="full" my={4}>
      <SegmentedPicker
        options={options}
        selection={PAYOUT_MODE.indexOf(mode)}
        onSelectionChange={(index) => {
          if (index !== 0) return;
        }}
      />
    </Box>
  );
};

const Balances = () => {
  const contract = useSelectedContract();
  const { data: balances } = useFasterPayoutsBalancesQuery(contract?.id);
  return <BalancesStrip balances={balances} />;
};

const TableCard = () => {
  const { t } = useTranslation(['makeFasterPayout']);
  return (
    <FullWidthBlock gridArea="table">
      <Card>
        <CardHead>
          <CardTitle>{t('makeFasterPayout:listTitle')}</CardTitle>
        </CardHead>
        <CardContent p={0}>
          <McFasterPayoutsTable />
        </CardContent>
      </Card>
    </FullWidthBlock>
  );
};

const FormCard = () => {
  const { t } = useTranslation(['makeFasterPayout']);
  return (
    <FullWidthBlock gridArea="form">
      <Card>
        <CardHead>
          <CardTitle>{t('makeFasterPayout:formTitle')}</CardTitle>
        </CardHead>
        <CardContent>
          <MakeFasterPayoutForm />
        </CardContent>
      </Card>
    </FullWidthBlock>
  );
};

const PageHeader = () => {
  const { t } = useTranslation(['common', 'makeFasterPayout']);
  const contract = useSelectedContract();
  return (
    <HStack
      alignItems="flex-end"
      justifyContent="space-between"
      mb={{ base: 2, sm: 5 }}
    >
      <div>
        <Breadcrumb separator={<>&mdash;</>}>
          <BreadcrumbItem>
            <BreadcrumbLink as={Link} to="/">
              {t('common:home')}
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <BreadcrumbLink as={Text}>{contract?.name}</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>
            <BreadcrumbLink as={Text}>
              {t('makeFasterPayout:title')}
            </BreadcrumbLink>
          </BreadcrumbItem>
        </Breadcrumb>
        <PageTitle mb={0}>{t('makeFasterPayout:title')}</PageTitle>
      </div>
    </HStack>
  );
};

export default MakeFasterPayoutPage;
